import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { graphql, withApollo } from 'react-apollo';
import { connect } from 'react-redux';
import { compose } from 'redux';
import memoize from 'memoize-one';
import abandonPreOrder from '../graphql/mutations/abandonPreOrder';
import createOrder from '../graphql/mutations/createOrder';
import publicCoupon from '../graphql/queries/publicCoupon';
import publicTicketTypes from '../graphql/queries/publicTicketTypes';
import publicFormFields from '../graphql/queries/publicFormFields';
import mapBy from '../lib/mapBy';
import noop from '../lib/noop';
import { mapDispatchToProps, mapStateToProps } from '../redux/registerExtension';
import RegisterExtension from './RegisterExtension';

class RegisterExtensionContainer extends Component {
  static propTypes = {
    client: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    site: PropTypes.object.isRequired,
    closeRegisterExtension: PropTypes.func.isRequired,
    dialogOpen: PropTypes.bool.isRequired,
    publicTicketTypesData: PropTypes.object,
    publicCouponData: PropTypes.object,
    currentStep: PropTypes.number.isRequired,
    nextRegisterExtensionStep: PropTypes.func.isRequired,
    language: PropTypes.string.isRequired,
    preOrder: PropTypes.object,
    preOrderTickets: PropTypes.array,
    installment: PropTypes.object,
  };

  static defaultProps = {
    publicTicketTypesData: undefined,
    publicCouponData: undefined,
    preOrder: undefined,
    preOrderTickets: undefined,
    installment: undefined,
  };

  state = {
    orderError: null,
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!nextProps.dialogOpen && this.props.dialogOpen) {
      this.setState({ orderError: null });
    }
  }

  createOrder = async values => {
    const {
      client,
      event: { id: eventId, registerHideBuyerFieldsOnFreeOrders },
      publicTicketTypesData: { publicTicketTypes },
      nextRegisterExtensionStep,
      preOrder,
      preOrderTickets,
      installment,
      language,
    } = this.props;

    const tickets = preOrderTickets.map((preOrderTicket, i) => ({
      ...values.tickets[i],
      ticketTypeId: preOrderTicket.id,
    }));

    const ticketTypesById = mapBy(publicTicketTypes, 'id');

    const buyer =
      preOrder.subtotalValue || !registerHideBuyerFieldsOnFreeOrders
        ? values.buyer
        : this.getBuyerFromTicket(values.tickets[0]);

    const input = {
      eventId,
      preOrderId: preOrder.id,
      tickets,
      subtotalValue: preOrder.subtotalValue,
      totalValue: preOrder.totalValue,
      buyer,
      paymentForm: values.paymentForm,
      language,
    };

    if (preOrder.totalValue > 0 && input.paymentForm === 'creditCard') {
      const { expiration, ...other } = values.creditCard;
      const [expiryMonth, expiryYear] = expiration.split('/');

      input.creditCard = {
        ...other,
        expiryMonth,
        expiryYear,
      };

      if (installment) {
        Object.assign(input, installment);
      }
    }

    try {
      this.setState({ orderError: null });

      const {
        data: { createOrder: order },
      } = await client.mutate({
        mutation: createOrder,
        variables: { input },
      });

      const orderTicketsWithQuantityByType = {};
      order.tickets.forEach(ticket => {
        if (orderTicketsWithQuantityByType[ticket.ticketTypeId]) {
          orderTicketsWithQuantityByType[ticket.ticketTypeId].quantity += 1;
        } else {
          orderTicketsWithQuantityByType[ticket.ticketTypeId] = {
            ...ticketTypesById[ticket.ticketTypeId],
            quantity: 1,
          };
        }
      });

      const orderTicketsWithQuantity = Object.values(orderTicketsWithQuantityByType);

      this.setState({ order, orderTicketsWithQuantity });
      nextRegisterExtensionStep();
    } catch (e) {
      this.setState({ orderError: e });
    }
  };

  getBuyerFromTicket = ticket => {
    const {
      event: { registerFormFieldIds },
      publicFormFieldsData: { publicFormFields = [] },
    } = this.props;

    const registerFormFields = this.registerFormFields(registerFormFieldIds, publicFormFields);

    const buyerFormFieldTypes = ['credentialName', 'email', 'phone'];
    const buyerFieldsByType = {
      credentialName: 'name',
      email: 'email',
      phone: 'phone',
    };
    const buyer = {};

    registerFormFields
      .filter(formField => buyerFormFieldTypes.includes(formField.type))
      .forEach(formField => {
        buyer[buyerFieldsByType[formField.type]] = ticket.person[formField.id];
      });

    return buyer;
  };

  cancelRegisterExtension = () => {
    const { client, closeRegisterExtension, currentStep, preOrder } = this.props;

    if (preOrder && currentStep < 3) {
      client
        .mutate({
          mutation: abandonPreOrder,
          variables: { id: preOrder.id },
        })
        .catch(noop);
    }

    closeRegisterExtension();
  };

  registerFormFields = memoize((registerFormFieldIds, formFields) => {
    const formFieldsById = mapBy(formFields, 'id');
    return registerFormFieldIds.map(id => formFieldsById[id]).filter(Boolean);
  });

  render() {
    const {
      publicTicketTypesData: { publicTicketTypes } = {},
      publicCouponData: { error } = {},
      publicFormFieldsData: { publicFormFields } = [],
      event,
    } = this.props;

    return (
      <RegisterExtension
        {...this.props}
        {...this.state}
        event={event}
        registerFormFields={this.registerFormFields(event.registerFormFieldIds, publicFormFields)}
        ticketTypes={publicTicketTypes}
        createOrder={this.createOrder}
        cancelRegisterExtension={this.cancelRegisterExtension}
        couponError={error}
      />
    );
  }
}

export default compose(
  withApollo,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  graphql(publicFormFields, {
    name: 'publicFormFieldsData',
    options: props => ({
      variables: {
        eventId: props.event.id,
        language: props.language,
        filter: [
          { field: '_id', values: [{ operator: 'in', value: props.event.registerFormFieldIds }] },
          { field: 'visibility', values: [{ operator: 'in', value: ['register'] }] },
        ],
      },
    }),
  }),
  graphql(publicCoupon, {
    name: 'publicCouponData',
    skip: props => !props.couponCode,
    options: props => ({ variables: { eventId: props.event.id, code: props.couponCode } }),
  }),
  graphql(publicTicketTypes, {
    name: 'publicTicketTypesData',
    skip: props => !props.dialogOpen,
    options: props => ({
      variables: {
        eventId: props.event.id,
        language: props.language,
        couponCode: props.coupon && props.coupon.code,
      },
      fetchPolicy: 'network-only',
    }),
  }),
)(RegisterExtensionContainer);



// WEBPACK FOOTER //
// ./src/site-render/src/RegisterExtension/RegisterExtensionContainer.js